/**
 * PHASE 8A — A8-P1-2 client-side validation for `max_reports_per_user`.
 *
 * Pure, dependency-free helper that mirrors (and strictly exceeds) the
 * server-side Zod schema in `backend/src/routes/admin.ts`.
 *
 * Behaviour required by Phase 8A spec:
 *   - Must be a finite integer.
 *   - Must be >= 1.
 *   - Must be <= 10000.
 *   - Empty / NaN / null / undefined / decimal / negative / zero / >10000
 *     are rejected.
 *
 * The function returns one of:
 *   - { ok: true;  value: number }  — caller may PATCH.
 *   - { ok: false; reason: string } — caller must NOT PATCH and must
 *                                     surface `reason` to the user.
 */

export interface MaxReportsValidationOk {
  ok: true;
  value: number;
}

export interface MaxReportsValidationErr {
  ok: false;
  reason: string;
}

export type MaxReportsValidation = MaxReportsValidationOk | MaxReportsValidationErr;

/** Lower bound (inclusive). */
export const MAX_REPORTS_MIN = 1;
/** Upper bound (inclusive). */
export const MAX_REPORTS_MAX = 10000;

/**
 * Validate a raw form value for `max_reports_per_user`.
 *
 * @param raw The form value (typically `string` from a `<input>`) or any
 *            value (number, null, undefined, etc.) coming from API responses.
 */
export function validateMaxReportsPerUser(raw: unknown): MaxReportsValidation {
  // null / undefined
  if (raw === null || raw === undefined) {
    return { ok: false, reason: 'Max reports per user is required.' };
  }

  // Empty string — must not silently coerce to 0.
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed === '') {
      return { ok: false, reason: 'Max reports per user is required.' };
    }
    // "null" / "NaN" / "Infinity" / non-numeric strings — explicitly rejected.
    if (
      trimmed.toLowerCase() === 'null' ||
      trimmed.toLowerCase() === 'nan' ||
      trimmed.toLowerCase() === 'infinity' ||
      trimmed.toLowerCase() === '-infinity'
    ) {
      return { ok: false, reason: 'Max reports per user must be a finite number.' };
    }
    // Reject "1.5" or "1e2" or anything that's not a digit-only integer.
    if (!/^-?\d+$/.test(trimmed)) {
      return { ok: false, reason: 'Max reports per user must be a whole number.' };
    }
    // We know it's an integer-shaped string. Cast and hand off to numeric checks.
    return validateMaxReportsPerUser(Number(trimmed));
  }

  // Numbers (and Number-coerced strings via the recursion above)
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) {
      return { ok: false, reason: 'Max reports per user must be a finite number.' };
    }
    if (Number.isNaN(raw)) {
      return { ok: false, reason: 'Max reports per user must be a finite number.' };
    }
    // Reject decimals: `Number.isInteger` is the canonical check, but be
    // doubly defensive.
    if (!Number.isInteger(raw)) {
      return { ok: false, reason: 'Max reports per user must be a whole number.' };
    }
    if (raw < MAX_REPORTS_MIN) {
      return { ok: false, reason: `Max reports per user must be at least ${MAX_REPORTS_MIN}.` };
    }
    if (raw > MAX_REPORTS_MAX) {
      return {
        ok: false,
        reason: `Max reports per user must be at most ${MAX_REPORTS_MAX}.`,
      };
    }
    return { ok: true, value: raw };
  }

  // Everything else (boolean, object, array, bigint, symbol, …) is invalid.
  return { ok: false, reason: 'Max reports per user must be a whole number.' };
}
